// What the billing screens are billing AGAINST.
//
// For most of this module's life that was always an IPD admission, and the
// assumption is baked in everywhere: 56 calls compose `ipd_admission/{id}/billing`,
// and the patient picker renders "Bed · Admission #". An emergency visit is now
// also billable — it opens a bed-less EMERGENCY encounter and posts through the
// same charge pipeline — but it has no admission, so none of those paths reach it
// and its charges were invisible to billing entirely. See ADR 0009.
//
// A subject names which kind we are dealing with and where its endpoints live.
//
// **The two kinds are NOT interchangeable, and this module says so out loud.**
// IPD exposes around seventy billing endpoints; emergency exposes a handful. A router
// that silently rewrote the base URL for all of them would turn sixty-odd
// unsupported operations into 404s at the worst possible moment — mid-invoice,
// with no explanation. So capability is declared here and checked before the
// call, and an unsupported operation fails with a sentence a developer can act
// on rather than a network error a clerk cannot.

export const SUBJECT_IPD = "ipd";
export const SUBJECT_EMERGENCY = "emergency";

// Everything an emergency visit can currently do on the billing side. Anything
// absent here is not "not yet wired" — it does not exist on the server.
const EMERGENCY_CAPABILITIES = new Set([
  "billing-setup",
  "charges",
  "charge-capture",
  "charge-reverse",
  "invoices",
  "payments",
]);

/**
 * The subject a selected patient context represents.
 *
 * Reads the explicit marker the emergency mapper sets. An older context
 * restored from sessionStorage carries no marker at all, and those are always
 * admissions — emergency subjects did not exist when they were written — so
 * absence means IPD rather than unknown.
 */
export function subjectKindOf(patient) {
  return patient?.subject_kind === SUBJECT_EMERGENCY ? SUBJECT_EMERGENCY : SUBJECT_IPD;
}

export const isEmergencySubject = (patient) => subjectKindOf(patient) === SUBJECT_EMERGENCY;

/** The id the subject's own endpoints are keyed on — an admission id, or a visit id. */
export function subjectIdOf(patient) {
  if (isEmergencySubject(patient)) {
    return patient?.visit_id ?? patient?.visitId ?? null;
  }
  return (
    patient?.admission_id ??
    patient?.admissionId ??
    patient?.ipd_admission_id ??
    null
  );
}

/**
 * Whether this subject supports an operation, named by the capability keys
 * above. An IPD admission supports everything the module offers; only emergency
 * is restricted, so only emergency is enumerated.
 */
export function subjectSupports(patient, capability) {
  if (!isEmergencySubject(patient)) return true;
  return EMERGENCY_CAPABILITIES.has(capability);
}

/**
 * The reason an operation is unavailable, for a caller that needs to explain
 * itself. Returns an empty string when the operation IS available, so it reads
 * as a guard: `const why = unsupportedReason(...); if (why) ...`.
 */
export function unsupportedReason(patient, capability) {
  if (subjectSupports(patient, capability)) return "";
  return (
    `"${capability}" is not available for an emergency visit — ` +
    "emergency billing supports charges, reversal, invoices and payments only."
  );
}
